const week3 = {
  number: 3,
  title: "The Residents",
  theme: "The patterns that live in your house",
  modules: [
    {
      id: "3.1",
      title: "What Is a Resident?",
      content: `The Inner House is not empty. It is inhabited.

A **Resident** is a stable psychological pattern—shaped by developmental stages, attachment experiences, repeated environments, and major emotional events. Each resident is a way of interpreting the world, a set of fears and preferences, a mode of survival and meaning-making.

Residents are not personalities or characters. You are not literally multiple people. But you contain multiple organized patterns, and each pattern has its own perspective that can dominate your experience in certain conditions.

Every resident carries:

- **Its own fears** — what it perceives as threat
- **Its own preferences** — what it wants and values
- **Its own interpretation style** — how it makes sense of events
- **Its own developmental age** — when it was formed

This last point is crucial. A resident formed in early childhood still operates from that developmental period. It sees the world as that child saw it. It has the resources that child had. When that resident activates, you may find yourself reacting with childlike intensity or vulnerability—because the pattern running your response was formed when you were a child.

**All residents believe they are protecting you.**

This is the key insight. No resident sees itself as the problem. Every resident believes its fears are valid, its preferences are important, and its way of interpreting the world is correct.

They are often wrong.

A resident formed in childhood may apply childhood logic to adult situations. A resident formed in trauma may perceive threat where none exists. A resident formed in a particular relationship may generalize patterns that only applied there.

But they are always trying to protect. Understanding this changes how you relate to them. They are not enemies to be defeated. They are patterns to be understood, updated, and—when possible—coordinated.`
    },
    {
      id: "3.2",
      title: "Trust and Autonomy",
      content: `The first two residents are foundational. All higher development depends on them.

**The Trust Resident**

The Trust Resident formed in your earliest experiences of attachment—the first year of life, before you had language, before you could distinguish yourself from your caregivers.

This resident asks one fundamental question: **"Am I safe?"**

Not "Am I safe right now?" but the deeper question: "Is the world a place where my needs will be met? Can I depend on others? Is it safe to relax?"

When the Trust Resident is secure, you can take risks. You can explore. You can tolerate uncertainty because at some deep level, you believe things will be okay.

When the Trust Resident is wounded, you are always on guard. You struggle to relax. You anticipate abandonment or betrayal. You cannot depend on others—or you depend too desperately.

The Trust Resident interprets the world through safety. A raised voice, a cancelled plan, a slow response to a text—all of these can activate the Trust Resident's ancient question: Am I safe? Will I be left?

**The Autonomy Resident**

The Autonomy Resident formed next—in the period when you first began to separate from caregivers, to say "no," to have preferences of your own.

This resident asks: **"Do I choose this?"**

The Autonomy Resident defines the boundary of self. It protects against intrusion. It insists on sovereignty—the right to determine your own actions, thoughts, and feelings.

When the Autonomy Resident is secure, you can set boundaries without guilt. You can say no. You can accept help without feeling controlled.

When the Autonomy Resident is wounded, you may struggle with boundaries—either unable to set them or rigid and defensive. You may experience any request as intrusion. You may feel controlled when no control is intended.

The Autonomy Resident interprets the world through sovereignty. A suggestion feels like a demand. A question feels like an interrogation. Help feels like a takeover.

**These two residents together create the foundation.** Trust allows connection; Autonomy allows independence. Both are necessary. Problems arise when they are wounded or when they conflict—when the need for safety opposes the need for sovereignty.`
    },
    {
      id: "3.3",
      title: "Initiative and Industry",
      content: `Built on the foundation of Trust and Autonomy, the next two residents enable action.

**The Initiative Resident**

The Initiative Resident formed in the period of early exploration—when you first began to have goals, to want to do things, to move toward objects and experiences.

This resident asks: **"What can I try?"**

The Initiative Resident is the source of curiosity, drive, and experimentation. It wants to move before knowing how. It thrives in uncertainty because uncertainty means possibility.

When the Initiative Resident is secure, you can begin things. You can tolerate not knowing. You can experiment without needing guaranteed success.

When the Initiative Resident is wounded, you may be paralyzed by perfectionism. You may need to know exactly how something will work before starting. You may be unable to try because trying might mean failing.

The Initiative Resident interprets the world through possibility. Every situation is a potential adventure or experiment.

**The Industry Resident**

The Industry Resident formed later—during the school years, when you learned that effort produces results, that skills can be developed, that discipline has rewards.

This resident asks: **"What must be done?"**

The Industry Resident transforms direction into achievement. It is the part of you that shows up, does the work, follows through. It takes the Initiative Resident's "what can I try" and answers: "Here is how."

When the Industry Resident is secure, you can sustain effort. You can tolerate the boring middle of any project. You can work without constant excitement or reward.

When the Industry Resident is wounded, you may feel perpetually incompetent. You may start many things and finish few. You may either avoid effort entirely or become compulsively workaholic—proving competence because you never actually believe you have it.

The Industry Resident interprets the world through competence and contribution. Every situation is evaluated: What can I accomplish here? What value can I add?

**Together, Initiative and Industry create the engine of action.** Initiative provides the spark; Industry provides the fuel. Without Initiative, you cannot begin. Without Industry, you cannot continue.`
    },
    {
      id: "3.4",
      title: "Identity and Intimacy",
      content: `**The Identity Resident**

The Identity Resident formed in adolescence—when you first asked, in a serious and sustained way, "Who am I?" It emerged through social comparison, through trying on different selves, through the work of assembling a coherent narrative about who you are and where you belong.

This resident asks: **"Who am I?"**

The Identity Resident organizes the story of the house. It is the part of you that maintains a sense of continuity—connecting who you were yesterday to who you are today to who you might become tomorrow.

When the Identity Resident is secure, you have a stable sense of self that can flex without breaking. You can absorb new experiences, integrate them into your narrative, and remain fundamentally yourself through change.

When the Identity Resident is wounded, you may experience identity confusion. You may change dramatically based on context—one person with family, another with friends, another at work—without a core that connects them. You may feel empty or inauthentic, as if you are always performing rather than being.

The Identity Resident interprets the world through self-definition and belonging. Every interaction is data about who you are.

**The Intimacy Resident**

The Intimacy Resident formed through deep relational experiences—the first times you truly let another person see you, the experiences of real vulnerability and connection.

This resident asks: **"Can I be seen?"**

The Intimacy Resident knows that you cannot navigate alone. It is the part of you that yearns for closeness, for shared meaning, for the experience of being fully known by another person.

When the Intimacy Resident is secure, you can be vulnerable without feeling destroyed. You can let others see your weaknesses, your fears, your authentic self—and trust that you will not be abandoned for it.

When the Intimacy Resident is wounded, you may avoid closeness entirely, keeping everyone at a distance where they cannot hurt you. Or you may merge with others, losing yourself in relationships because you cannot tolerate the vulnerability of being seen as separate.

The Intimacy Resident interprets the world through connection and vulnerability. Every relationship is evaluated: Is it safe to be seen here? Can I truly know and be known?

**Identity and Intimacy often create tension.** To let yourself be truly seen (Intimacy) requires a self to be seen (Identity). But intimacy often changes identity. The negotiation between "Who am I?" and "Can I be seen?" is lifelong.`
    },
    {
      id: "3.5",
      title: "Generativity",
      content: `**The Generativity Resident**

The Generativity Resident is the last of the core residents to form—emerging through maturity, through the experience of creating and contributing to something larger than yourself.

This resident asks: **"What can I build?"**

The Generativity Resident cares about legacy. It is the part of you that wants to contribute, to mentor, to create things that will outlast you. It builds beyond the self.

When the Generativity Resident is secure, you can invest in the future. You can mentor without needing credit. You can build things you will not live to see completed.

When the Generativity Resident is wounded, you may feel stagnant—going through the motions of life without sense of purpose or contribution. You may be unable to invest in others because you feel depleted. You may hoard rather than give, protect rather than create.

The Generativity Resident interprets the world through impact and meaning. Every action is evaluated: Does this matter? Will this last? Am I contributing or merely consuming?

**The Generativity Resident often emerges in midlife—when the earlier questions have been addressed (or at least managed) and a new question becomes urgent: What will I leave behind?**

But Generativity can activate earlier or later depending on circumstances. Someone who faces their mortality young may encounter Generativity earlier. Someone who has been focused on survival may not encounter it until later.

**The Residents as a System**

All seven residents coexist. They are not stages you pass through and leave behind. They are parts of you that remain, activating in different circumstances, competing for leadership, interpreting every experience through their own lens.

- Trust asks: Am I safe?
- Autonomy asks: Do I choose this?
- Initiative asks: What can I try?
- Industry asks: What must be done?
- Identity asks: Who am I?
- Intimacy asks: Can I be seen?
- Generativity asks: What can I build?

Every situation, every decision, every moment activates some subset of these questions. Understanding the residents means hearing these questions—recognizing which resident is speaking, which fears are activated, which interpretation is dominating.

The goal is not to silence any resident. The goal is coordination.`
    }
  ],
  reflection: {
    prompt: "Which resident do you hear most often? Which resident do you rarely hear from? What might that quiet resident need in order to speak?",
    placeholder: "Consider which residents dominate your experience and which seem absent or suppressed. This is the beginning of understanding your internal system."
  }
};

export default week3;
